import {
	ANIME_LIST_SLUG,
	DEFAULT_PAGE_SIZE,
	HIDDEN_GENRE_SLUGS,
	SEARCH_API_PAGE_SIZE,
	SEARCH_MAX_API_PAGES,
} from '@/lib/constants';
import { ApiError, buildApiUrl, fetchJson } from '@/lib/http';
import type {
	ApiListResponse,
	ApiMovieDetailResponse,
	ApiMovieListItem,
	ApiTaxonomyResponse,
} from '@/types/api';
import type {
	AnimeDetail,
	AnimeListFilters,
	AnimePage,
	AnimeSearchCatalog,
	Taxonomy,
} from '@/types/anime';
import { isAnimeItem, mapDetail, mapPagination, mapSummary, mapTaxonomy } from './mappers';

/**
 * Danh sách anime. Luôn đi `/v1/api/danh-sach/hoat-hinh` (type list),
 * thể loại/quốc gia/năm chỉ là bộ lọc phụ trên kho đó.
 */
export async function fetchAnimePage(
	filters: AnimeListFilters = {},
	signal?: AbortSignal,
): Promise<AnimePage> {
	const url = buildApiUrl(`/v1/api/danh-sach/${ANIME_LIST_SLUG}`, {
		page: filters.page ?? 1,
		limit: filters.limit ?? DEFAULT_PAGE_SIZE,
		sort_field: filters.sortField ?? 'modified.time',
		sort_type: filters.sortType ?? 'desc',
		sort_lang: filters.sortLang,
		category: filters.category,
		country: filters.country,
		year: filters.year,
	});

	const payload = await fetchJson<ApiListResponse>(url, signal);
	return toAnimePage(payload);
}

/**
 * Tìm kiếm. `/v1/api/tim-kiem` trả mọi loại phim và không có query type.
 * `category=hoat-hinh` là lọc THỂ LOẠI — sẽ sót anime không gắn tag đó.
 * Gom vài trang API rồi giữ `type === hoathinh`, phân trang phía client.
 */
export async function searchAnimeCatalog(
	keyword: string,
	signal?: AbortSignal,
): Promise<AnimeSearchCatalog> {
	const trimmed = keyword.trim();
	if (!trimmed) {
		return { items: [], resultCapped: false };
	}

	const collected: ApiMovieListItem[] = [];
	let cdnBase: string | undefined;
	let apiPage = 1;
	let apiTotalPages = 1;

	while (apiPage <= apiTotalPages && apiPage <= SEARCH_MAX_API_PAGES) {
		const url = buildApiUrl('/v1/api/tim-kiem', {
			keyword: trimmed,
			page: apiPage,
			limit: SEARCH_API_PAGE_SIZE,
		});
		const payload = await fetchJson<ApiListResponse>(url, signal);
		const rawItems = payload.data?.items;

		if (!Array.isArray(rawItems)) {
			throw new ApiError('Máy chủ phim trả về danh sách không hợp lệ.', 502, url);
		}

		cdnBase = payload.data?.APP_DOMAIN_CDN_IMAGE ?? cdnBase;
		collected.push(
			...rawItems.filter((item) => item?.slug && isAnimeItem(item)),
		);

		const pagination = payload.data.params?.pagination;
		apiTotalPages = mapPagination(pagination).totalPages || apiPage;
		apiPage += 1;
	}

	return {
		items: collected.map((item) => mapSummary(item, cdnBase)),
		resultCapped: apiPage - 1 < apiTotalPages,
	};
}

export function sliceSearchCatalog(
	catalog: AnimeSearchCatalog,
	page = 1,
	limit = DEFAULT_PAGE_SIZE,
): AnimePage {
	const currentPage = Math.max(1, page);
	const start = (currentPage - 1) * limit;
	const totalItems = catalog.items.length;
	const totalPages = totalItems === 0 ? 0 : Math.ceil(totalItems / limit);

	return {
		items: catalog.items.slice(start, start + limit),
		pagination: {
			totalItems,
			perPage: limit,
			currentPage,
			totalPages,
		},
		resultCapped: catalog.resultCapped,
	};
}

export async function fetchAnimeDetail(
	slug: string,
	signal?: AbortSignal,
): Promise<AnimeDetail> {
	const url = buildApiUrl(`/phim/${encodeURIComponent(slug)}`);
	const payload = await fetchJson<ApiMovieDetailResponse>(url, signal);

	if (!payload.movie?.slug || payload.status === false) {
		throw new ApiError(
			payload.msg || 'Không tìm thấy phim này.',
			404,
			url,
		);
	}

	if (!isAnimeItem(payload.movie)) {
		throw new ApiError(
			'NekoStream chỉ phát phim hoạt hình. Tựa này không thuộc kho anime.',
			404,
			url,
		);
	}

	return mapDetail(payload.movie, payload.episodes);
}

export async function fetchGenres(signal?: AbortSignal): Promise<Taxonomy[]> {
	const payload = await fetchJson<ApiTaxonomyResponse>(
		buildApiUrl('/the-loai'),
		signal,
	);
	return mapTaxonomy(payload.data?.items).filter(
		(genre) => !HIDDEN_GENRE_SLUGS.has(genre.slug),
	);
}

export async function fetchCountries(signal?: AbortSignal): Promise<Taxonomy[]> {
	const payload = await fetchJson<ApiTaxonomyResponse>(
		buildApiUrl('/quoc-gia'),
		signal,
	);
	return mapTaxonomy(payload.data?.items);
}

function toAnimePage(payload: ApiListResponse): AnimePage {
	const cdnBase = payload.data?.APP_DOMAIN_CDN_IMAGE;
	const rawItems = payload.data?.items;

	if (!Array.isArray(rawItems)) {
		throw new ApiError('Máy chủ phim trả về danh sách không hợp lệ.', 502, '');
	}

	return {
		items: rawItems
			.filter((item) => item?.slug && isAnimeItem(item))
			.map((item) => mapSummary(item, cdnBase)),
		pagination: mapPagination(payload.data.params?.pagination),
	};
}
